import { brandMsg, overMsg, priceMsg, t, underMsg } from '../i18n/index.js';
import type { Filters } from '../../components/ProductGridCoCo/ProductGridFilterCoCo.js';

export const MAX_DEFAULT_VISIBLE_OPTIONS = 7;
const MINIMUM_PRODUCTS_FOR_PRICE_FILTER = 5;

export const matchesSearch = (search: string, texts: string[]) => {
  const keywords = search.toLowerCase().split(' ').filter(Boolean);
  const searchableKeywords = texts.flatMap(text => text.toLowerCase().split(' ')).filter(Boolean);
  return keywords.every(keyword => searchableKeywords.some(text => text.includes(keyword)));
};

export const getRange = (range: string) => {
  const [lower, upper] = range.split('-');
  return { lower: Number.parseFloat(lower), upper: Number.parseFloat(upper) };
};

export const roundUpToStep = (price: number, step: number) => Math.round((price + step) / step) * step;

export const createPriceFilterLabel = ({ lower, upper }: { lower: number; upper: number }, i: number, size: number) => {
  if (i === 0) {
    return `${t.A5B3(underMsg)} ${upper} €`;
  }
  if (i + 1 === size) {
    return `${t.JX0H(overMsg)} ${lower} €`;
  }
  return `${lower} - ${upper} €`;
};

export const createRangeOptions = (ranges: Array<{ lower: number; upper: number }>) =>
  ranges.map(({ lower, upper }, i) => ({
    value: () => `${lower}-${upper}`,
    label: createPriceFilterLabel({ lower, upper }, i, ranges.length),
  }));

export const isPriceInSelectedRanges = (price: number | undefined, selections: string[]) =>
  price !== undefined &&
  selections
    .map(getRange)
    .some(({ lower, upper }) => (price === 0 && lower === 0) || (price >= lower && price <= upper));

export interface BrandAndPriceFilterConfig<T> {
  getBrand: (item: T) => string;
  getPriceEuros: (item: T) => number | undefined;
  priceRangeSize: number;
  priceRoundingStep: number;
}

export const createBrandAndPriceFilters = <T>(
  items: T[],
  { getBrand, getPriceEuros, priceRangeSize, priceRoundingStep }: BrandAndPriceFilterConfig<T>
): Filters<T> => {
  const brands = Array.from(new Set(items.map(getBrand).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const prices = items.map(getPriceEuros).filter((price): price is number => price !== undefined);
  const ranges: Array<{ lower: number; upper: number }> = [];
  if (prices.length >= MINIMUM_PRODUCTS_FOR_PRICE_FILTER) {
    const maxPrice = Math.max(...prices);
    ranges.push({ lower: 0, upper: roundUpToStep(Math.min(...prices), priceRoundingStep) });
    for (
      let lower = ranges[0].upper;
      ranges.length < MAX_DEFAULT_VISIBLE_OPTIONS && lower < maxPrice;
      lower += priceRangeSize
    ) {
      ranges.push({ lower, upper: lower + priceRangeSize });
    }
    if (ranges.length === MAX_DEFAULT_VISIBLE_OPTIONS) {
      ranges[ranges.length - 1].upper = maxPrice;
    }
  }
  return {
    FILTER_BRAND: {
      displayText: () => t.GV78(brandMsg),
      options: brands.map(brand => ({ value: () => brand })),
      selectedOptions: [],
      filterFn: (item, selections) => selections.includes(getBrand(item)),
      visible: brands.length > 1,
      hasTooManyOptions: brands.length > MAX_DEFAULT_VISIBLE_OPTIONS,
    },
    FILTER_PRICE: {
      displayText: () => t.V72N(priceMsg),
      options: createRangeOptions(ranges),
      selectedOptions: [],
      filterFn: (item, selections) => isPriceInSelectedRanges(getPriceEuros(item), selections),
      visible: ranges.length > 1,
    },
  };
};

export const hasActiveFilters = <T>(filters: Filters<T>) => Object.values(filters).some(filter => filter.visible);

export const gridFilter =
  <T>(filters: Filters<T>) =>
  (item: T) =>
    Object.values(filters).every(
      filter => filter.selectedOptions.length === 0 || filter.filterFn(item, filter.selectedOptions)
    );

export const getFilterKeys = <T>(filters: Filters<T>) => Object.keys(filters) as Array<keyof Filters<T>>;

export const getSelectedFilterCount = <T>(filters: Filters<T>) =>
  Object.values(filters).reduce((count, filter) => count + filter.selectedOptions.length, 0);

export const clearFilterSelections = <T>(filters: Filters<T>): Filters<T> => ({
  FILTER_BRAND: { ...filters.FILTER_BRAND, selectedOptions: [] },
  FILTER_PRICE: { ...filters.FILTER_PRICE, selectedOptions: [] },
});

export const getSelectedOptionsStillAvailable = <T>(
  previous: Filters<T>,
  rebuilt: Filters<T>,
  key: keyof Filters<T>
) => {
  const options = new Set(rebuilt[key].options.map(option => option.value()));
  return previous[key].selectedOptions.filter(option => options.has(option));
};

export const keepSelectedFilterOptions = <T>(previous: Filters<T>, rebuilt: Filters<T>): Filters<T> => {
  getFilterKeys(rebuilt).forEach(key => {
    rebuilt[key].selectedOptions = getSelectedOptionsStillAvailable(previous, rebuilt, key);
  });
  return rebuilt;
};
